// ─────────────────────────────────────────────
// Oike Method Library — Ale's knowledge base (techniques, questions, examples,
// messages, objection handling). The AI reads the active entries every time it
// writes or recommends: P3 and P5 of the Diagnostic, the message generators,
// the proposal generators and the coaching session proposals (coaching_sessions).
// Never the scoring prompts (P1, P2, P4): the rubric is not part of the library
// and changes only in diagnosticConfig.js.
//
// Shared by the panel (src/components/MethodLibrary.jsx) and the server
// (netlify/functions/method-library.mts, shared/methodLibrary.ts).
// Table: "Method Library" in the central base (docs/diagnostic/airtable-schema.md).
// ─────────────────────────────────────────────

export const METHOD_APPLIES_TO = [
  { key: 'diagnostic',   label: 'Diagnostic' },
  { key: 'systemDesign', label: 'System Design' },
  { key: 'enablement',   label: 'Enablement' },
  { key: 'advisory',     label: 'Advisory' },
  { key: 'messages',     label: 'Mensajes' },
  { key: 'proposals',    label: 'Propuestas' },
  // Acompañamiento: where a tool is used in the software (not its area or modality)
  { key: 'coaching_sessions',   label: 'Sesiones de acompañamiento' },
  { key: 'coaching_programs',   label: 'Programas' },
  { key: 'coaching_activities', label: 'Actividades entre sesiones' },
  // Mi espacio › Mi marca: tools Ale may choose, one by one, as the base of a piece of her personal brand
  { key: 'brand', label: 'Mi marca' },
];

/** Places of the coaching module: an entry there is a tool and needs area and modality. */
export const COACHING_PLACES = ['coaching_sessions', 'coaching_programs', 'coaching_activities'];

/** Kind of tool (separate from where it is used, its area and its modality). */
export const METHOD_TOOL_TYPES = [
  { key: 'question', label: 'Pregunta' },
  { key: 'exercise', label: 'Ejercicio' },
  { key: 'practice', label: 'Práctica' },
  { key: 'model',    label: 'Modelo' },
  { key: 'guide',    label: 'Guía' },
];

/** Thematic area: a tool can belong to several. Business and sales are specialties. */
export const METHOD_AREAS = [
  { key: 'personal',     label: 'Personal' },
  { key: 'professional', label: 'Profesional' },
  { key: 'business',     label: 'Negocios' },
  { key: 'sales',        label: 'Ventas' },
];

export const METHOD_MODALITIES = [
  { key: 'individual', label: 'Individual' },
  { key: 'team',       label: 'Equipo' },
  { key: 'both',       label: 'Individual y equipo' },
];

export const METHOD_STATUSES = [
  { key: 'draft',    label: 'Borrador' },
  { key: 'active',   label: 'Activa' },
  { key: 'archived', label: 'Archivada' },
];

export const METHOD_SOURCE_TYPES = [
  { key: 'course',     label: 'Curso' },
  { key: 'book',       label: 'Libro' },
  { key: 'podcast',    label: 'Podcast' },
  { key: 'video',      label: 'Video' },
  { key: 'article',    label: 'Artículo' },
  { key: 'own_note',   label: 'Nota propia' },
  { key: 'experience', label: 'Experiencia' },
];

export const METHOD_ORIGINS = ['manual', 'from_notes', 'from_source'];

// ── Por tipo de cliente (Fase 7) ──
// Every criterion is optional. An entry with none of industry, team size, sale
// type or country is general and serves every client. An entry with a criterion
// the client does not match (another industry, another country…) never enters.

/** Same list as the industry field of the landing form (public/home.html); landing = the value that form saves. */
export const METHOD_INDUSTRIES = [
  { key: 'saas',          label: 'SaaS / Software',                  landing: 'SaaS / Software' },
  { key: 'fintech',       label: 'Fintech',                          landing: 'Fintech' },
  { key: 'edtech',        label: 'Edtech',                           landing: 'Edtech' },
  { key: 'healthtech',    label: 'Healthtech',                       landing: 'Healthtech' },
  { key: 'logistics',     label: 'Logística / Supply Chain',         landing: 'Logística / Supply Chain' },
  { key: 'consulting',    label: 'Consultoría / Servicios',          landing: 'Consultoría / Servicios profesionales' },
  { key: 'marketing',     label: 'Marketing / Agencia',              landing: 'Marketing / Agencia' },
  { key: 'ecommerce',     label: 'E-commerce / Retail',              landing: 'E-commerce / Retail' },
  { key: 'manufacturing', label: 'Manufactura / Industria',          landing: 'Manufactura / Industria' },
  { key: 'construction',  label: 'Construcción / Real estate',       landing: 'Construcción / Real estate' },
  { key: 'distribution',  label: 'Distribución / Mayoristas',        landing: 'Distribución / Mayoristas' },
  { key: 'other',         label: 'Otro',                             landing: 'Otro' },
];

/** The same buckets as teamSizeBucket() in pricing.js. */
export const METHOD_TEAM_SIZES = [
  { key: '1-2',   label: '1-2 personas' },
  { key: '3-10',  label: '3-10 personas' },
  { key: '11-30', label: '11-30 personas' },
  { key: '30+',   label: 'Más de 30' },
];

export const METHOD_SALE_TYPES = [
  { key: 'transactional', label: 'Transaccional (ticket chico, ciclo corto)' },
  { key: 'complex',       label: 'Compleja (ticket alto, varios decisores)' },
];
/** A won-deal cycle of this many days or more is a complex sale (Ale can change it per diagnostic). */
export const COMPLEX_SALE_CYCLE_DAYS = 90;

/** Regions group pricing.js country keys. Mexico is only in latam. */
export const METHOD_REGIONS = {
  latam:        ['mexico', 'chile', 'uruguay', 'argentina', 'bolivia', 'colombia', 'peru', 'ecuador', 'paraguay', 'brazil', 'venezuela'],
  europe:       ['spain', 'uk'],
  northAmerica: ['usa', 'canada'],
};
export const METHOD_COUNTRY_LABELS = {
  usa: 'Estados Unidos', canada: 'Canadá', uk: 'Reino Unido', spain: 'España', mexico: 'México', chile: 'Chile', uruguay: 'Uruguay',
  argentina: 'Argentina', bolivia: 'Bolivia', colombia: 'Colombia', peru: 'Perú', ecuador: 'Ecuador', paraguay: 'Paraguay',
  brazil: 'Brasil', venezuela: 'Venezuela',
  latam: 'Región: Latinoamérica', europe: 'Región: Europa', northAmerica: 'Región: Norteamérica',
};
/** Country keys (those of pricing.js) first, then the regions. */
export const METHOD_COUNTRIES = Object.keys(METHOD_COUNTRY_LABELS).map(key => ({ key, label: METHOD_COUNTRY_LABELS[key] }));

export const METHOD_ENTRY_TYPES = [
  { key: 'technique', label: 'Técnica' },
  { key: 'framework', label: 'Framework (más largo, entra entero o no entra)' },
];

/** System Design modules (docs/diagnostic/system-design.md). */
export const METHOD_SD_MODULES = [
  { key: 'icp',       label: 'ICP' },
  { key: 'process',   label: 'Proceso' },
  { key: 'pipeline',  label: 'Pipeline' },
  { key: 'messaging', label: 'Messaging' },
  { key: 'crmData',   label: 'CRM y datos' },
  { key: 'ritual',    label: 'Ritual y métricas' },
  { key: 'accounts',  label: 'Cuentas' },
];

/** The four client criteria, in the order they are shown. */
export const METHOD_CRITERIA = [
  { key: 'industries', client: 'industry' },
  { key: 'teamSizes',  client: 'teamSize' },
  { key: 'saleTypes',  client: 'saleType' },
  { key: 'countries',  client: 'country' },
];

export const labelOfIn = (list, key) => list.find(x => x.key === key)?.label || key;

/** The entry as the panel and the API see it → Airtable field names. */
export const METHOD_FIELDS = {
  title: 'Title',
  technique: 'Technique',
  whenToUse: 'When To Use',
  whenNotToUse: 'When Not To Use',
  example: 'Example',
  appliesTo: 'Applies To',
  industries: 'Industries',
  teamSizes: 'Team Size',
  saleTypes: 'Sale Type',
  countries: 'Countries',
  entryType: 'Entry Type',
  sdModules: 'SD Modules',
  sourceType: 'Source Type',
  source: 'Source',
  status: 'Status',
  clientVisible: 'Client Visible',
  version: 'Version',
  origin: 'Origin',
  approvedBy: 'Approved By',
  approvedAt: 'Approved At',
  updatedBy: 'Updated By',
  updatedAt: 'Updated At',
  history: 'Version History',
  created: 'Created',
  recordId: 'Record Id',
};

/**
 * Tool fields (added by scripts/coaching-migration.mjs). The code works before
 * the migration: it reads them only if they exist and writes them only when
 * they have a value, so the current entries and generators keep working.
 */
export const METHOD_TOOL_FIELDS = {
  toolType: 'Tool Type',
  purpose: 'Purpose',
  topics: 'Topics',
  areas: 'Areas',
  modality: 'Modality',
  steps: 'Steps',
  duration: 'Duration',
  materials: 'Materials',
  openingQuestions: 'Opening Questions',
  closingQuestions: 'Closing Questions',
  adaptation: 'Adaptation',
  facilitationNotes: 'Facilitation Notes',
  scopeNote: 'Scope Note',
  requiresTraining: 'Requires Training',
  sourceRefs: 'Source Refs',
  observations: 'Observations',
};

/** Limits of the tool text fields. */
export const METHOD_TOOL_TEXT_LIMITS = {
  purpose: 800,
  steps: 2000,
  duration: 60,
  materials: 800,
  openingQuestions: 1000,
  closingQuestions: 1000,
  adaptation: 1500,
  facilitationNotes: 1500,
  scopeNote: 800,
};
export const METHOD_TOPICS_MAX = 12;
export const METHOD_TOPIC_MAX_CHARS = 40;
export const METHOD_OBSERVATION_MAX = 1000;
export const METHOD_OBSERVATIONS_MAX = 50;

/** What a coaching tool needs before approval, on top of METHOD_REQUIRED. */
export const METHOD_TOOL_REQUIRED = ['purpose'];

/** Method Sources: what was saved vs. what was actually provided as text. */
export const SOURCE_KINDS = [
  { key: 'book',       label: 'Libro' },
  { key: 'podcast',    label: 'Podcast' },
  { key: 'video',      label: 'Video' },
  { key: 'course',     label: 'Curso' },
  { key: 'article',    label: 'Artículo' },
  { key: 'own_note',   label: 'Nota propia' },
  { key: 'experience', label: 'Experiencia' },
];
export const SOURCE_PROCESSING = [
  { key: 'reference_only',   label: 'Solo referencia', hint: 'Está guardada, pero no hay texto: la IA no la leyó ni abrió el enlace.' },
  { key: 'content_provided', label: 'Con contenido aportado', hint: 'Tiene tus notas o fragmentos. La IA solo puede trabajar con ese texto.' },
  { key: 'processed',        label: 'Procesada', hint: 'La IA propuso herramientas a partir del texto aportado (no del libro, video o enlace completo).' },
];
export const SOURCE_TEXT_LIMITS = {
  title: 200,
  creator: 200,
  reference: 500,
  notes: 20000,
  excerpt: 4000,
  locator: 80,
};
export const SOURCE_EXCERPTS_MAX = 20;

/** Maximum characters per text field, so one entry never takes the whole prompt budget. */
export const METHOD_TEXT_LIMITS = {
  title: 120,
  technique: 1500,
  whenToUse: 800,
  whenNotToUse: 800,
  example: 1500,
  source: 200,
};
/**
 * A framework admits more text (BANT, MEDDPICC, a full qualification method).
 * The most it can render (about 5,800 characters with its labels) still fits the
 * Diagnostic cap, so a framework always enters whole or not at all.
 */
export const METHOD_TEXT_LIMITS_FRAMEWORK = {
  title: 120,
  technique: 3000,
  whenToUse: 800,
  whenNotToUse: 800,
  example: 1000,
  source: 200,
};
export const methodTextLimits = (entryType) => (entryType === 'framework' ? METHOD_TEXT_LIMITS_FRAMEWORK : METHOD_TEXT_LIMITS);
/** The text fields that an entry needs before it can be approved. */
export const METHOD_REQUIRED = ['title', 'technique', 'whenToUse', 'example'];

/** "Crear desde mis notas": the notes are never stored, only the entries Ale saves. */
export const METHOD_NOTES_MAX = 20000;
export const METHOD_PROPOSALS_MAX = 5;

/**
 * Characters of library text per prompt. Whole entries only, most recent first;
 * the ones that do not fit are left out (and counted in the log).
 */
export const METHOD_CONTEXT_MAX = {
  diagnostic: 6000,
  systemDesign: 8000,
  messages: 3000,
  proposals: 3000,
  coaching_sessions: 8000,
  brand: 4000,
};

/** Generators that use the library and the "dónde aplica" value each one reads. */
export const METHOD_FUNCTIONS = {
  messages: 'messages',
  proposals: 'proposals',
  campaign_runner: 'messages',
};

export const methodLabel = (list, key) => list.find(x => x.key === key)?.label || key;

/**
 * Files Ale adds to a source are read in the browser and kept as text
 * excerpts; the file itself is never uploaded. Only .txt, .md and .docx are
 * read. Everything else gets a clear message instead of a silent failure.
 */
export function sourceFileKind(name) {
  const ext = String(name || '').toLowerCase().match(/\.([a-z0-9]+)$/)?.[1] || '';
  if (['txt', 'md', 'markdown'].includes(ext)) return 'text';
  if (ext === 'docx') return 'docx';
  if (ext === 'pdf') return 'pdf';
  if (['mp3', 'm4a', 'wav', 'ogg', 'aac', 'flac', 'mp4', 'mov', 'm4v', 'webm', 'avi', 'mkv'].includes(ext)) return 'media';
  return 'unsupported';
}
export const SOURCE_FILE_MESSAGES = {
  pdf: 'Los PDF no se procesan: abre el PDF, copia el fragmento que te sirve y pégalo como fragmento, con la página.',
  media: 'El audio y el video no se transcriben ni se escuchan: pega tus notas o la transcripción, con el minuto.',
  unsupported: 'Este formato no se puede leer. Usa .txt, .md o .docx, o pega el texto como fragmento.',
  link: 'Los enlaces se guardan solo como referencia: la IA no los abre. Para que trabaje con el contenido, pega notas o fragmentos.',
};
